<template>
  <div
    class="tw-flex tw-flex-col tw-gap-1 tw-py-3"
    :data-test="`compliance-row-${comp.name}`"
  >
    <div class="tw-flex tw-justify-between tw-items-center">
      <div class="tw-flex tw-flex-col tw-gap-0.5">
        <WdsStack direction-row align-center gap-xs>
          <span class="tw-text-lg tw-leading-none" :class="dotColorClass">&#x2022;</span>
          <WdsText :text="comp.title" medium />
          <WdsTooltip v-if="!comp.completed" global :z-index="50" :text="comp.description">
            <WdsIcon name="help" :size="14" class="tw-text-gray-400" />
          </WdsTooltip>
        </WdsStack>
        <div class="tw-ml-5">
          <WdsText
            v-if="comp.completed && completedAtFormatted"
            :text="completedAtFormatted"
            secondary
            sm
          />
          <WdsText
            v-else-if="!comp.completed"
            :text="$t('No information provided yet.')"
            secondary
            sm
          />
        </div>
      </div>
      <WdsButtonGhost
        v-if="files.length > 0"
        class="tw-flex-shrink-0"
        data-test="compliance-row-download"
        :title="$t('Download files')"
        @click="downloadFiles"
      >
        <div class="tw-flex tw-items-center tw-gap-1">
          <WdsIcon :name="WdsIconNameEnum.DOCUMENT" :size="16" class="tw-text-gray-600" />
          <WdsText :text="String(files.length)" secondary sm />
        </div>
      </WdsButtonGhost>
    </div>
    <dl
      v-if="detailFields.length > 0"
      class="tw-ml-5 tw-grid tw-grid-cols-[max-content_1fr] tw-gap-x-3 tw-gap-y-0.5"
      data-test="compliance-row-details"
    >
      <template v-for="(field, index) in detailFields" :key="`${field.label}-${index}`">
        <dt><WdsText :text="`${field.label}:`" secondary sm /></dt>
        <dd>
          <WdsText
            :text="field.value ?? $t('Not provided')"
            :secondary="field.value === null"
            sm
          />
        </dd>
      </template>
    </dl>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { FragmentType, useFragment } from '@/gql'
import type {
  ComplianceDataBusinessEntity,
  ComplianceDataField,
  ComplianceDataFile,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { COMPLIANCE_ROW_FRAGMENT } from '@/components/RecruiterDetailPanel/graphql/fragments'
import useDate from '@/composables/useDate'

const props = defineProps<{
  compliance: FragmentType<typeof COMPLIANCE_ROW_FRAGMENT>
}>()

const comp = computed(() => useFragment(COMPLIANCE_ROW_FRAGMENT, props.compliance))

const dotColorClass = computed(() =>
  comp.value.completed ? 'tw-text-green-500' : 'tw-text-gray-400',
)

const { date: completedAtDate, isValid: completedAtIsValid } = useDate(
  computed(() => comp.value.completedAt),
)
const completedAtFormatted = computed(() =>
  completedAtIsValid.value ? $t('Updated :date', { date: completedAtDate.value }) : null,
)

const files = computed(() =>
  comp.value.data.filter((d): d is ComplianceDataFile => d.__typename === 'ComplianceDataFile'),
)

interface DetailField {
  label: string
  value: string | null
}

const detailFields = computed<DetailField[]>(() => {
  const result: DetailField[] = []

  for (const entry of comp.value.data) {
    if (entry.__typename === 'ComplianceDataField') {
      const field = entry as ComplianceDataField
      result.push({ label: field.label, value: field.value ?? null })
    } else if (entry.__typename === 'ComplianceDataBusinessEntity') {
      const be = (entry as ComplianceDataBusinessEntity).businessEntity
      if (be.localisedName) {
        result.push({ label: $t('Entity type'), value: be.localisedName })
      }
      if (be.companyName !== null && be.companyName !== undefined) {
        result.push({ label: $t('Company name'), value: be.companyName })
      }
      if (be.companyNumber !== null && be.companyNumber !== undefined) {
        result.push({ label: $t('Company number'), value: be.companyNumber })
      }
      if (be.taxNumber !== null && be.taxNumber !== undefined) {
        result.push({ label: $t('Tax number'), value: be.taxNumber })
      }
    }
  }

  return result
})

function downloadFiles() {
  for (const item of files.value) {
    window.open(item.file.url, '_blank')
  }
}
</script>
